/**
 * The two cart line dropdowns, shared by the mini cart (mini-cart.js) and the cart page
 * (<x-cart-line-controls>). Both own their own mutation so the two surfaces cannot drift in what
 * a change actually does; the caller supplies only the loading and error plumbing it already has.
 */
import { useMemo } from 'preact/hooks'
import { getInventoryLimit } from 'helpers/cart-inventory.mjs'
import { resolveLineSizeUnit, buildLineSizeOptions, lineProperties } from 'helpers/size-units.mjs'
import { cart as cartClass } from 'helpers/cart'

/** Shopify allows more, but a dropdown past ten stops being a dropdown. */
export const MAX_LINE_QUANTITY = 10

/**
 * The upsell card's field styling (product-upsell-render.liquid): a notched caption sitting on the
 * border, over an appearance-none bordered select. Exported because the cart page reuses them for
 * a one-size line, which shows the size as a static box rather than a select.
 */
export const FIELD_CAPTION_CLASS =
  'absolute -top-[7px] left-[7px] z-[1] bg-primary-white px-1 text-[9px] font-medium leading-[14px] text-neutrals-tertiary-grey'
export const FIELD_BOX_CLASS =
  'h-10 w-full appearance-none rounded border border-solid cursor-pointer border-neutrals-medium-grey bg-primary-white py-1 pl-3 pr-8 paragraph-xs text-primary-black'

/**
 * `caption` is the visible notch and stays short enough to sit in 9px type; `label` is the
 * accessible name and carries the detail the notch cannot, such as which unit sizes are in.
 */
const CartSelect = ({ caption, label, value, options, onChange, disabled, className = '' }) => (
  <label class={`relative block ${className}`}>
    <span class={FIELD_CAPTION_CLASS}>{caption}</span>
    <select
      class={FIELD_BOX_CLASS}
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={onChange}
    >
      {options.map((option) => (
        <option key={option.id} value={option.id} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
)

/**
 * Line quantity, 1 to ten or the tracked stock, whichever is lower. Removal stays with the
 * Remove link rather than a 0 option, so a mis-scroll on mobile cannot empty a line.
 */
export const CartLineQuantitySelect = ({ item, disabled, onStart, onError, onDone }) => {
  const options = useMemo(() => {
    const limit = getInventoryLimit(item)
    const max = Math.max(1, Math.min(MAX_LINE_QUANTITY, limit ?? MAX_LINE_QUANTITY))
    /** A line already over the cap (added before it, or by quick add) keeps its value selectable. */
    const ceiling = Math.max(max, item.quantity)
    return Array.from({ length: ceiling }, (_, i) => ({
      id: String(i + 1),
      label: String(i + 1),
      disabled: i + 1 > max && i + 1 !== item.quantity
    }))
  }, [item.quantity, item.inventory_quantity, item.inventory_management, item.inventory_policy])

  const onChange = async (event) => {
    const quantity = Number(event.target.value)
    try {
      onStart?.()
      await cartClass.updateLineItemQuantity({ key: item.key, id: item.variant_id, quantity }, { silent: true })
      onDone?.()
    } catch (error) {
      onError?.(error)
    }
  }

  return (
    <CartSelect
      caption="Qty"
      label="Quantity"
      value={String(item.quantity)}
      options={options}
      onChange={onChange}
      disabled={disabled}
      className="w-20"
    />
  )
}

/** A line is only swappable when its product offers a second size at the line's width. */
export const hasSizeChoice = (item) => buildLineSizeOptions(item, resolveLineSizeUnit(item)).length > 1

/**
 * Size swap for a line, labelled in the unit the line was added in. Renders nothing when the
 * product has a single size, which is every non-footwear line.
 */
export const CartLineSizeSelect = ({ item, disabled, onStart, onError, onDone }) => {
  const unit = useMemo(() => resolveLineSizeUnit(item), [item.properties, item.sizing_tag])
  const options = useMemo(() => buildLineSizeOptions(item, unit), [item.size_variants, item.variant_id, unit])

  if (options.length < 2) return null

  const onChange = async (event) => {
    const variantId = event.target.value
    if (String(variantId) === String(item.variant_id)) return
    try {
      onStart?.()
      await cartClass.swapLineItemVariant({
        key: item.key,
        variantId,
        quantity: item.quantity,
        properties: lineProperties(item)
      })
      onDone?.()
    } catch (error) {
      onError?.(error)
    }
  }

  return (
    <CartSelect
      caption="Size"
      label={`Size (${unit})`}
      value={String(item.variant_id)}
      options={options}
      onChange={onChange}
      disabled={disabled}
      className="w-28"
    />
  )
}

/** The unit a line's size is displayed in, for the static "Size:" line beside the dropdown. */
export const lineSizeLabel = (item) => {
  const unit = resolveLineSizeUnit(item)
  return buildLineSizeOptions(item, unit).find((option) => option.selected)?.label ?? null
}
